import { DragVertical as DragIcon, Fullscreen as FullscreenIcon } from 'mdi-material-ui';
import { useEffect } from 'react';
import { Box } from '../Box';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { useContainerContext } from './ContainerContextProvider';
import { ContainerMenu } from './ContainerMenu';
import { ActionsVisibility } from './model';

type UseShouldShowActionsResponse = {
  showActions: boolean;
  showDraggableAction: boolean;
  showOpenAction: boolean;
};

const useShouldShowActions = (): UseShouldShowActionsResponse => {
  const { actions, menuItems, modalOpen, draggable } = useContainerContext();
  const showOpenAction = typeof modalOpen !== 'undefined';
  const showDraggableAction = draggable === true;

  return {
    showActions: !!actions || !!menuItems || showOpenAction || showDraggableAction,
    showOpenAction,
    showDraggableAction,
  };
};

export interface ContainerActionsProps {}

/**
 * Container actions that may be used to render actions on containers without a
 * header. When a header is used, the actions are automatically managed by the header.
 */
export const ContainerActions = () => {
  const { isHovered, setIsHovered, actionsSize, isShowingFloatingActions, setIsShowingFloatingActions } =
    useContainerContext();

  const shouldShowActions = useShouldShowActions();

  const height = actionsSize === 'medium' ? 40 : 32;

  useEffect(() => {
    // Setting this as a separate value that's passed up to context enables us to
    // change the top-right border radius on the container to be 0 when the actions
    // are open.
    setIsShowingFloatingActions(isHovered && shouldShowActions.showActions);
  }, [isHovered, setIsShowingFloatingActions, shouldShowActions.showActions]);

  if (!shouldShowActions.showActions) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'inherit',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        position: 'absolute',
        height: `${height}px`,
        top: `-${height}px`,
        right: 0,
        padding: 0.5,
        visibility: isShowingFloatingActions ? 'initial' : 'hidden',
      }}
      onMouseEnter={() => {
        // keep actions visible when mouse enter actions section
        setIsHovered(true);
      }}
      className="floating-actions"
      data-testid="floating-actions"
    >
      <InternalContainerActions actionsVisibility="hover" actionsSpacing={1} />
    </Box>
  );
};

export interface InternalContainerActionsProps {
  actionsVisibility: ActionsVisibility;
  /**
   * Override the default actions spacing.
   */
  actionsSpacing?: number;
  /**
   * Support for deprecated `actions` prop on `ContainerHeader`.
   * @deprecated
   */
  deprecatedActions?: React.ReactNode;
}

const OPEN_MODAL_TEXT = 'Open in fullscreen modal';
const DRAG_HANDLE_TEXT = 'Move';

/**
 * Internal container actions automatically used by `ContainerHeader`.
 */
export const InternalContainerActions = ({
  actionsVisibility,
  actionsSpacing,
  deprecatedActions,
}: InternalContainerActionsProps) => {
  const { actions, menuItems, isHovered, isMenuOpen, actionsSize, onOpenModalButtonClick } = useContainerContext();

  const shouldShowActions = useShouldShowActions();

  if (!shouldShowActions.showActions && !deprecatedActions) {
    return null;
  }

  const showActions = actionsVisibility === 'always' || isHovered || isMenuOpen;

  const defaultActionsSpacing = actionsSize === 'medium' ? 1 : 0.5;

  return (
    <Stack
      direction="row"
      spacing={actionsSpacing ?? defaultActionsSpacing}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="flex-end"
      sx={{
        // This can't use `display:none` because the parent element needs to be kept around while
        // the menu fades away, otherwise the menu will jump to the corner of the screen.
        visibility: showActions ? 'initial' : 'hidden',
        // We temporarily position the actions using `absolute` positioning when the actions are hidden
        // so that the text from the title can flow into the space the actions normally occupy.
        position: showActions ? 'relative' : 'absolute',
        // This isn't strictly necessary, but when invisible, we position the actions in the same location
        // they normally are to prevent potential future click-conflicts with them being on the left side where the
        // title is. The actions are invisible in this state and it could be difficult to debug issues.
        right: showActions ? 'auto' : 0,
      }}
    >
      {actions || deprecatedActions}
      {shouldShowActions.showOpenAction && (
        <Tooltip Content={OPEN_MODAL_TEXT}>
          <IconButton
            size={actionsSize}
            aria-label={OPEN_MODAL_TEXT}
            onClick={() => {
              onOpenModalButtonClick?.();
            }}
            className="OpenInModalAction"
          >
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
      )}
      {!!menuItems && <ContainerMenu size={actionsSize}>{menuItems}</ContainerMenu>}
      {shouldShowActions.showDraggableAction && (
        <Tooltip Content={DRAG_HANDLE_TEXT}>
          <IconButton size={actionsSize} aria-label={DRAG_HANDLE_TEXT}>
            <DragIcon className="drag-handle" style={{ cursor: 'grab' }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
